/**
 * Housing SDG Contribution — /housing/sdg
 * Uses useHousingJourney() — single source of truth.
 *
 * FIX (P0): journey, org were referenced but never destructured from the hook.
 * Removed dead variables: actions2, actionsLocal, demo.
 * Added null guards for all data paths.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft } from "lucide-react";
import { useHousingJourney } from "@/hooks/useHousingJourney";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const SDG_COLORS = {
  7:  { bg: "bg-yellow-50",  border: "border-yellow-200",  text: "text-yellow-800",  dot: "bg-yellow-400"  },
  9:  { bg: "bg-orange-50",  border: "border-orange-200",  text: "text-orange-800",  dot: "bg-orange-400"  },
  11: { bg: "bg-teal-50",    border: "border-teal-200",    text: "text-teal-800",    dot: "bg-teal-400"    },
  12: { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",   dot: "bg-amber-400"   },
  13: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
};
const REL_COLOR = {
  High:   "bg-emerald-100 text-emerald-700 border-emerald-300",
  Medium: "bg-amber-100   text-amber-700   border-amber-300",
};

export default function HousingSDGPage() {
  const navigate = useNavigate();

  // All data from single source of truth — useHousingJourney
  const { loading, sdgTags, actions, org, journey } = useHousingJourney();

  // Loading guard
  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  // Null guards — page must never crash if data is missing
  const sdgs        = sdgTags   || [];
  const safeActions = actions   || [];
  const safeOrg     = org       || {};

  // No-data state
  if (!journey) return (
    <div className="p-6 lg:p-8 space-y-6">
        <button onClick={navigate.bind(null, "")} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors">← Housing Project</button>
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <p className="text-sm text-slate-500">SDG data not yet available. Run a calculation first.</p>
        </div>
    </div>
  );

  const highSdgs   = sdgs.filter(s => s.relevance === "High");

  return (
    <div className="p-6 lg:p-8 space-y-6">

        <button onClick={navigate.bind(null, "")} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors">← Housing Project</button>

      <PageHeader
        label="PROJECT WORKSPACE"
        title="SDG Contribution"
        backUrl="/housing-project"
        backLabel="Housing Project"
      />

        {/* Summary narrative */}
        <div className="bg-[#0F172A] rounded-xl p-6 mb-6">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Project Summary</p>
          <p className="text-sm text-white leading-relaxed">
            {safeOrg.name} contributes to <span className="text-teal-400 font-semibold">{sdgs.length} UN Sustainable Development Goals</span> through
            its low-carbon housing programme. Embodied carbon reduction via material substitution supports
            SDG 9 and SDG 13 at high confidence. The rooftop solar programme contributes to SDG 7.
            Sustainable construction at programme scale contributes to SDG 11 and SDG 12.
          </p>
          {sdgs.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {sdgs.map(s => {
                const c = SDG_COLORS[s.id] || SDG_COLORS[13];
                return (
                  <span key={s.id} className={`text-xs font-bold px-2.5 py-1 rounded-full border ${c.bg} ${c.border} ${c.text}`}>
                    {s.name}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* SDG table — only when data exists */}
        {sdgs.length > 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
            <div className="px-6 py-4 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-900">Sustainability Contribution (SDGs)</p>
              <p className="text-xs text-slate-400 mt-0.5">Relevance and driver for each SDG attribution</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {["SDG", "Title", "Relevance", "Driver", "Source Action"].map(h => (
                      <th key={h} className="text-left text-xs text-slate-400 uppercase tracking-wider font-bold px-5 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sdgs.map((s, i) => {
                    const c   = SDG_COLORS[s.id] || SDG_COLORS[13];
                    const rel = s.relevance;
                    const srcAction = safeActions.find(a =>
                      (a.sdg_tags || []).some(t => t.id === s.id)
                    );
                    return (
                      <tr key={s.id} className={`border-b border-slate-50 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"}`}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${c.dot}`} />
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${c.bg} ${c.border} ${c.text}`}>
                              {s.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-xs font-semibold text-slate-900">{s.title || s.name}</p>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${REL_COLOR[rel] || REL_COLOR.Medium}`}>
                            {rel}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-xs text-slate-600">{s.driver}</p>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-xs text-slate-400 italic">
                            {srcAction ? srcAction.title.split("(")[0].trim() : "Programme design"}
                          </p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 border-t border-slate-100">
              <p className="text-xs text-slate-400 italic">
                SDG alignment is indicative — based on project design interventions and reduction actions.
                Not a certified sustainability assessment.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center mb-5">
            <p className="text-sm text-slate-400">No SDG data available for this project.</p>
          </div>
        )}

        {/* High relevance detail cards */}
        {highSdgs.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {highSdgs.map(s => {
              const c = SDG_COLORS[s.id] || SDG_COLORS[13];
              const relatedActions = safeActions.filter(a => (a.sdg_tags || []).some(t => t.id === s.id));
              return (
                <div key={s.id} className={`rounded-xl p-5 border ${c.bg} ${c.border}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className={`text-xs font-bold ${c.text}`}>{s.name}</span>
                      <p className="text-sm font-semibold text-slate-900">{s.title || s.name}</p>
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${REL_COLOR.High}`}>High</span>
                  </div>
                  <p className="text-xs text-slate-600 mb-2">{s.driver}</p>
                  {relatedActions.map(a => (
                    <div key={a.title} className="flex items-center gap-1.5 text-xs text-slate-500">
                      <span className="text-teal-500">→</span>
                      <span>{a.title.split("(")[0].trim()}: {a.impact_tco2e} {a.carbon_type === "operational" ? "tCO₂e/yr" : "tCO₂e"} saved</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
